import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { MAX_QUOTE_CHARS } from './MessageReference';
import { selectedQuoteText } from './quoteDOM';
import SelectionReplyToolbar from './SelectionReplyToolbar';

function selectionArea(root, selection, markedOnly) {
  if (!selection || selection.isCollapsed || !root || !root.contains(selection.anchorNode) || !root.contains(selection.focusNode)) return null;
  const parent = (node) => node?.nodeType === 1 ? node : node?.parentElement;
  const start = parent(selection.anchorNode)?.closest('[data-quote-source]');
  const end = parent(selection.focusNode)?.closest('[data-quote-source]');
  if (start || end) return start === end && root.contains(start) ? start : null;
  return markedOnly ? null : root;
}

/** Prepare only the selected body; the portal owns subsequent scroll updates. */
export default function QuotableText({ message, messageId = message?.id, role = 'assistant', textRef: providedRef, onReply, source, proof, label, markedOnly = false, children }) {
  const ownRef = useRef(null);
  const textRef = providedRef || ownRef;
  const toolbarRef = useRef(null);
  const sourceKey = source ? JSON.stringify(source) : '';
  useEffect(() => {
    toolbarRef.current?.show(null);
  }, [messageId, proof, sourceKey]);
  const inspectSelection = (event) => {
    if (!onReply || !messageId) return;
    const root = textRef.current;
    const selection = root?.ownerDocument.defaultView?.getSelection();
    const area = selectionArea(root, selection, markedOnly);
    const quote = area ? selectedQuoteText(selection, area).slice(0, MAX_QUOTE_CHARS) : '';
    if (!quote.trim()) { toolbarRef.current?.show(null); return; }
    let selectedSource = source;
    if (area.dataset.quoteSource) {
      try { selectedSource = JSON.parse(area.dataset.quoteSource); }
      catch { toolbarRef.current?.show(null); return; }
    }
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const bounds = area.getBoundingClientRect();
    toolbarRef.current?.show({
      quote, area, source: selectedSource, proof, label: area.dataset.quoteLabel || label, range: range?.cloneRange(),
      anchorNode: selection.anchorNode, anchorOffset: selection.anchorOffset,
      focusNode: selection.focusNode, focusOffset: selection.focusOffset,
      point: { top: event?.clientY || bounds.top, bottom: event?.clientY || bounds.top, left: event?.clientX || bounds.left },
    });
  };
  return (
    <Box>
      <Box
        ref={textRef}
        data-message-quotable="true"
        data-quote-source-scope={markedOnly ? 'marked' : undefined}
        data-quote-source={!markedOnly && source ? sourceKey : undefined}
        data-quote-label={!markedOnly ? label : undefined}
        onMouseUp={inspectSelection}
        onKeyUp={inspectSelection}
      >
        {children}
      </Box>
      <SelectionReplyToolbar ref={toolbarRef} onReply={onReply} messageId={messageId} role={role} />
    </Box>
  );
}
